import { useEffect, useState } from 'react';
import {
  canonicalKpiPeriod,
  canonicalKpiSection,
  FieldType,
  FormulaParseError,
  FormulaTypeError,
  KpiArgSlot,
  parse,
  print,
  spanOf,
  tokenize,
  transformFields,
  typecheck,
  ValueType,
} from '../../formula-engine';
import {
  displayPathKey,
  FieldRefTarget,
  FormulaVocabulary,
  isFormulaRecord,
  normalizeDisplaySegment,
} from '../../util/formula-vocabulary';
import { FormulaSerialized } from './serialization';

export type FormulaValidation =
  | { state: 'empty' }
  /**
   * `incomplete` marks a formula that simply is not finished yet — an open
   * call, a dangling operator, an unclosed "[". It never makes the formula
   * savable; it lets the editor present work in progress neutrally instead of
   * flagging every keystroke on the way to a valid formula.
   */
  | { state: 'error'; message: string; range?: { start: number; end: number }; incomplete?: boolean }
  | { state: 'ok'; canonical: string; referenced: string[] };

/** How long a formula error waits, after the last edit, before it is shown. */
export const ERROR_SURFACE_DELAY_MS = 600;

/**
 * Editor-style error surfacing: non-error states pass through immediately
 * (errors clear the moment the formula is fixed), while errors surface only
 * after a typing pause — and are held back entirely while `holdErrors` is
 * true (a typeahead menu is open). Save-gating should use the live validation,
 * not this.
 */
export function useSurfacedValidation(validation: FormulaValidation, holdErrors: boolean): FormulaValidation {
  const [surfaced, setSurfaced] = useState<FormulaValidation>(validation);
  useEffect(() => {
    if (validation.state !== 'error') {
      setSurfaced(validation);
      return;
    }
    if (holdErrors) {
      return;
    }
    const timer = setTimeout(() => setSurfaced(validation), ERROR_SURFACE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [validation, holdErrors]);
  return surfaced;
}

/** A resolution failure that knows which field ref (in source order) it is about, so the editor can underline it. */
class FormulaRefError extends Error {
  constructor(
    message: string,
    public readonly fieldIndex: number
  ) {
    super(message);
  }
}

/**
 * The display-text span of the n-th field ref. Chips and typed refs alike
 * tokenize to one field token (string literals lex separately, so a bracketed
 * name inside quotes never counts), so the n-th field token in the display
 * text is the n-th ref the validator saw in the canonical text.
 */
function fieldRange(display: string, index: number): { start: number; end: number } | undefined {
  const token = tokenize(display).filter((candidate) => candidate.type === 'field')[index];
  return token ? { start: token.position, end: token.end } : undefined;
}

/**
 * A span in the canonical text moved into the display text. The two differ
 * only inside field refs (chips print their canonical path in one and their
 * display path in the other), and the n-th field token is the same ref in
 * both, so a position shifts by the length difference of every ref before
 * it — and a position inside a ref lands at the same place inside its twin.
 */
function displayRange(
  display: string,
  canonical: string,
  span: { start: number; end: number }
): { start: number; end: number } | undefined {
  try {
    const displayFields = tokenize(display).filter((token) => token.type === 'field');
    const canonicalFields = tokenize(canonical).filter((token) => token.type === 'field');
    if (displayFields.length !== canonicalFields.length) {
      return undefined;
    }
    const map = (position: number): number => {
      let shift = 0;
      for (let i = 0; i < canonicalFields.length; i++) {
        const field = canonicalFields[i];
        const twin = displayFields[i];
        if (position >= field.end) {
          shift += twin.end - twin.position - (field.end - field.position);
        } else if (position > field.position) {
          return twin.position + Math.min(position - field.position, twin.end - twin.position);
        } else {
          break;
        }
      }
      return position + shift;
    };
    return { start: map(span.start), end: map(span.end) };
  } catch {
    return undefined;
  }
}

type Vocabulary = Pick<FormulaVocabulary, 'displayNameIndex' | 'displayPathIndex' | 'byPath'>;

/**
 * Candidates are named by display path — what the user would type — unless
 * two share one (same name twice in a record), where only the canonical path
 * tells them apart.
 */
function ambiguous(ref: string, candidates: FieldRefTarget[], advice: string, at: number): FormulaRefError {
  const displayPaths = candidates.map((t) => t.displayPath);
  const names =
    new Set(displayPaths).size === candidates.length ? displayPaths : candidates.map((t) => t.path);
  return new FormulaRefError(`"[${ref}]" is ambiguous — it matches ${names.join(', ')}. ${advice}`, at);
}

/**
 * In KPI's name slot a display name means a KPI first, shadowing same-named
 * fields. Undefined when the slot is not a KPI name or no KPI matches — the
 * general resolution then runs, so a plain field resolves and typecheck says
 * "is not a KPI" rather than "unknown".
 */
function resolveKpiSlotName(
  kpiSlot: KpiArgSlot | undefined,
  trimmed: string,
  vocabulary: Vocabulary,
  at: number
): FieldRefTarget | undefined {
  if (kpiSlot !== 'name') {
    return undefined;
  }
  const kpis = (vocabulary.displayNameIndex.get(normalizeDisplaySegment(trimmed).toLowerCase()) ?? []).filter(
    (candidate) => candidate.kpi
  );
  if (kpis.length > 1) {
    throw ambiguous(trimmed, kpis, 'Rename one of those KPIs.', at);
  }
  return kpis[0];
}

/**
 * A non-referenceable string field can only be an id-select (other strings
 * are always referenceable); everything else is an unsupported type.
 */
function notReferenceable(target: { displayPath: string; type: string }, at: number): FormulaRefError {
  return new FormulaRefError(
    target.type === 'string'
      ? `"[${target.displayPath}]" holds an internal id — it can't be referenced in formulas`
      : `"[${target.displayPath}]" is a ${target.type} field — formulas can reference number, text, TRUE/FALSE and date fields only`,
    at
  );
}

/** A record is a namespace, not a value: point at one of its fields instead. */
function isRecordError(record: FieldRefTarget, vocabulary: Vocabulary, at: number): FormulaRefError {
  const example = [...vocabulary.byPath.values()].find(
    (candidate) => candidate.parent === record.path && candidate.referenceable
  );
  return new FormulaRefError(
    `"[${record.displayPath}]" is a record — pick one of its fields${
      example ? `, e.g. [${example.displayPath}]` : ''
    }`,
    at
  );
}

/**
 * Hand-typed ref → entry. A dotted ref is a display path: "[Company.Fund]"
 * (segments normalized, any case) names exactly one entry, or lists the
 * entries it matches. A single-segment name means a field by display name: inside
 * REDUCE(…, [L], LAMBDA(…)) — and in INDEX([L], index, …)'s third argument —
 * a member of L first (innermost list first), then a row/company field;
 * elsewhere only row/company fields are candidates, with KPI entries last (a
 * KPI name outside KPI() only resolves when nothing else matches, so
 * typecheck can teach the placement without shadowing same-named fields). A
 * known but non-referenceable match still reports its typed error, and a
 * list or member in the wrong place resolves — typecheck explains it by
 * placement. A record resolves too; the caller rejects it.
 */
function resolveDisplayName(
  ref: string,
  lists: string[],
  vocabulary: Vocabulary,
  at: number,
  kpiName = false
): FieldRefTarget {
  const trimmed = ref.trim();
  const pathKey = displayPathKey(trimmed);
  if (pathKey.includes('.')) {
    const byDisplayPath = vocabulary.displayPathIndex.get(pathKey) ?? [];
    if (byDisplayPath.length === 1) {
      return byDisplayPath[0];
    }
    if (byDisplayPath.length > 1) {
      throw ambiguous(ref, byDisplayPath, 'Rename one of those fields or reference it by path.', at);
    }
  }
  const candidates = vocabulary.displayNameIndex.get(normalizeDisplaySegment(trimmed).toLowerCase()) ?? [];
  for (const list of lists) {
    const member = candidates.find((candidate) => candidate.list?.path === list && candidate.referenceable);
    if (member) {
      return member;
    }
  }
  const rowLevel = candidates.filter(
    (candidate) => !candidate.list && !candidate.kpi && candidate.referenceable
  );
  if (rowLevel.length > 1) {
    throw ambiguous(ref, rowLevel, 'Rename one of those fields or reference it by path.', at);
  }
  if (rowLevel.length === 1) {
    return rowLevel[0];
  }
  const kpis = candidates.filter((candidate) => candidate.kpi);
  if (kpis.length === 1) {
    return kpis[0];
  }
  if (kpis.length > 1) {
    throw ambiguous(ref, kpis, 'Rename one of those KPIs.', at);
  }
  // A lone out-of-scope member still resolves so typecheck can explain the
  // placement; several would make the message name an arbitrary list.
  const members = candidates.filter((candidate) => candidate.list && candidate.referenceable);
  if (members.length > 1) {
    throw ambiguous(ref, members, 'Rename one of those fields or reference it by path.', at);
  }
  if (members.length === 1) {
    return members[0];
  }
  if (candidates.length > 0) {
    throw notReferenceable(candidates[0], at);
  }
  if (/(^|\.)customData\./.test(trimmed)) {
    throw new FormulaRefError(`"[${ref}]" — customData fields can't be referenced yet`, at);
  }
  if (kpiName) {
    throw new FormulaRefError(`Unknown KPI "[${ref}]" — use a KPI's name, e.g. [Revenue]`, at);
  }
  throw new FormulaRefError(
    `Unknown field "[${ref}]" — pick a record, then one of its fields, e.g. [Metrics.FMV]`,
    at
  );
}

/** The vocabulary entry as the engine's placement rules see it, with display paths for messages. */
function toFieldType(target: FieldRefTarget | undefined): FieldType | undefined {
  if (!target) {
    return undefined;
  }
  const label = target.displayPath;
  if (target.kpi) {
    return { kind: 'kpi', label, periods: target.kpi.periods, sections: target.kpi.sections };
  }
  if (target.type === 'list') {
    return { kind: 'list', label };
  }
  if (!target.referenceable) {
    return { kind: 'other', type: target.type, label };
  }
  const type = scalarType(target.type);
  if (!type) {
    return { kind: 'other', type: target.type, label };
  }
  if (target.list) {
    return { kind: 'member', list: target.list.path, type, label, listLabel: target.list.displayPath };
  }
  return { kind: 'scalar', type, label };
}

const VALUE_TYPES = new Set<string>(['number', 'string', 'boolean', 'date']);

/** A vocabulary entry's yup type as a formula value type, or undefined for anything else. */
export function scalarType(type: string): ValueType | undefined {
  return VALUE_TYPES.has(type) ? (type as ValueType) : undefined;
}

/**
 * The editor emits two renderings of one state: `display` (chips as display
 * paths — exactly what the user sees) and `canonical` (chips as record paths).
 * Syntax is checked against `display` so the error caret matches the input;
 * resolution runs on `canonical`, where chip refs are already exact paths and
 * only hand-typed refs still resolve — path-first (a path a formula stored
 * before records used counts, and canonicalizes to the record path), then by
 * display path or name with the enclosing REDUCE's list in scope. The
 * engine's typecheck then enforces where lists and their members may appear.
 * Resolution and type errors are located in the display text by the
 * offending ref's position among the refs.
 */
export function validateFormula(serialized: FormulaSerialized, vocabulary: Vocabulary): FormulaValidation {
  if (!serialized.display.trim()) {
    return { state: 'empty' };
  }
  try {
    parse(serialized.display);
  } catch (err) {
    if (err instanceof FormulaParseError) {
      // An unfinished formula carries no range: nothing is wrong at the caret
      // yet, and underlining the tail would contradict the neutral state.
      return err.incomplete
        ? { state: 'error', message: err.message, incomplete: true }
        : {
            state: 'error',
            message: err.message,
            range: { start: err.position, end: err.endPosition },
          };
    }
    return { state: 'error', message: (err as Error).message };
  }
  try {
    const ast = parse(serialized.canonical);
    const referenced: string[] = [];
    let nextField = 0; // transformFields calls back in source order
    const canonicalAst = transformFields(ast, (ref, lists, kpiSlot) => {
      const at = nextField++;
      const trimmed = ref.trim();
      if (kpiSlot === 'period' || kpiSlot === 'scenario') {
        // Enum-like tokens, not fields: canonicalize the known ones; unknown
        // ones pass through so typecheck flags them with the argument's own
        // message. Neither counts as a "referenced" field.
        const canonical = kpiSlot === 'period' ? canonicalKpiPeriod(trimmed) : canonicalKpiSection(trimmed);
        return canonical ?? trimmed;
      }
      const target =
        vocabulary.byPath.get(trimmed) ??
        resolveKpiSlotName(kpiSlot, trimmed, vocabulary, at) ??
        resolveDisplayName(ref, lists, vocabulary, at, kpiSlot === 'name');
      if (isFormulaRecord(target)) {
        throw isRecordError(target, vocabulary, at);
      }
      if (!target.referenceable && target.type !== 'list') {
        throw notReferenceable(target, at);
      }
      referenced.push(target.displayName);
      return target.path;
    });
    typecheck(canonicalAst, (path) => toFieldType(vocabulary.byPath.get(path)));
    return { state: 'ok', canonical: print(canonicalAst), referenced };
  } catch (err) {
    // A rule about an expression points at its span; one about a ref, at the n-th field token.
    const span = err instanceof FormulaTypeError && err.node ? spanOf(err.node) : undefined;
    const at =
      err instanceof FormulaRefError || (err instanceof FormulaTypeError && err.fieldIndex >= 0)
        ? err.fieldIndex
        : undefined;
    return {
      state: 'error',
      message: (err as Error).message,
      range:
        (span && displayRange(serialized.display, serialized.canonical, span)) ??
        (at === undefined ? undefined : fieldRange(serialized.display, at)),
    };
  }
}
